import { Disclosure } from "@headlessui/react";
import { Tooltip } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import { Fragment } from 'react';
import { Link, useLocation } from "react-router-dom";
import DocsPopup from "./DocsPopup";
import useWhatsNewRSS from "./whats-new-library/useWhatsNewRSS";

const CoreVersion = () => (
	<>
		<div className="flex items-center">
			<span>{astra_admin.version}</span>
			<span className="ml-1 sm:ml-2 text-[0.625rem] leading-[1rem] font-medium border border-slate-400 rounded-[0.1875rem] relative inline-flex flex-shrink-0 py-[0rem] px-1.5">
				{" "}
				{__("CORE", "astra")}{" "}
			</span>
		</div>
		{astra_admin.pro_available && (
			<div className="flex items-center pl-3">
				<span>{astra_admin.plugin_ver}</span>
				<span className="ml-1 sm:ml-2 text-[0.625rem] leading-[1rem] font-medium text-white border border-slate-800 bg-slate-800 rounded-[0.1875rem] relative inline-flex flex-shrink-0 py-[0rem] px-1.5">
					{" "}
					{__("PRO", "astra")}{" "}
				</span>
			</div>
		)}
		{wp.hooks.applyFilters(
			"astra_dashboard.after_navigation_version",
			<span />
		)}
	</>
);

export default function MainNav() {

	// Initialize our library hook.
	useWhatsNewRSS({
		rssFeedURL: astra_admin.astraWebsite?.whatsNewFeedUrl,
		selector: "#astra-whats-new",
		triggerButton: {
			beforeBtn: '<div class="w-8 sm:w-10 h-8 sm:h-10 flex items-center whitespace-nowrap justify-center cursor-pointer rounded-full border border-slate-200">',
			icon: '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M8.16667 3.90182V15.0335C8.16667 15.8434 7.51008 16.5 6.70015 16.5C6.08038 16.5 5.52752 16.1104 5.31907 15.5267L3.53039 10.4024M14 9.83333C15.3807 9.83333 16.5 8.71404 16.5 7.33333C16.5 5.95262 15.3807 4.83333 14 4.83333M3.53039 10.4024C2.33691 9.89508 1.5 8.71194 1.5 7.33333C1.5 5.49238 2.99238 4 4.83333 4H6.36007C9.77727 4 12.7141 2.97159 14 1.5L14 13.1667C12.7141 11.6951 9.77727 10.6667 6.36007 10.6667L4.83331 10.6667C4.37098 10.6667 3.93064 10.5725 3.53039 10.4024Z" stroke="#475569" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
			afterBtn: '</div>'
		},
		flyout: {
			title: __( "What's New?", "astra" ),
			formatDate: (/** @type Date */ date) => {
				const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
				const month = date.toLocaleDateString('en-US', { month: 'long' });
				const day = date.getDate();
				const year = date.getFullYear();

				// Format the date string
				const formattedDate = `${dayOfWeek} ${month} ${day}, ${year}`;

				return formattedDate;
			}
		}
	});

	const navMenus = [
		{
			name: __("Welcome", "astra"),
			slug: astra_admin.home_slug,
			path: "",
		},
		{
			name: __("Settings", "astra"),
			slug: astra_admin.home_slug,
			path: "settings",
		},
		{
			name: __("Free vs Pro", "astra"),
			slug: astra_admin.home_slug,
			path: "free-vs-pro",
		},
	];

	// insert starter templates menu if not white labeled and if promotion enabled.
	if ( ! astra_admin.is_whitelabel && astra_admin?.starter_templates_data?.is_promoting ) {
		navMenus.splice(2, 0, {
			name: astra_admin.starter_templates_data.title,
			slug: astra_admin.home_slug,
			path: "starter-templates",
		});
	}

	const redirectUpgradeAstraProPurchase = () => {
		window.open(
			astra_admin.astra_cta_btn_url,
			'_blank'
		);
	}

	const menus = wp.hooks.applyFilters( 'astra_dashboard.main_navigation', navMenus );

	const query = new URLSearchParams( useLocation()?.search );
	const activePage = query.get("page") || astra_admin.home_slug;
	const activePath = query.get("path") || "";

	if ( activePath === 'spectra' ) {
		return <></>;
	}

	return (
		<Disclosure as="nav" className="bg-white border-b border-slate-200">
			<div className="max-w-3xl mx-auto px-3 sm:px-6 lg:max-w-full">
				<div className="relative flex flex-col lg:flex-row justify-between h-28 lg:h-16 py-3 lg:py-0">
					<div className="lg:flex-1 flex items-start justify-center sm:items-stretch sm:justify-start">
						<a
							href={astra_admin.astra_base_url}
							className="flex-shrink-0 flex items-center"
						>
							<img
								className="lg:block h-[2.6rem] w-auto"
								src={astra_admin.logo_url}
								alt="Workflow"
							/>
						</a>
						<div className="ml-4 sm:ml-8 sm:flex gap-y-4 gap-x-8">
							{ menus.map( ( menu, key ) => (
								<Fragment key={ `?page=${ menu.slug }&path=${ menu.path }` }>
									{ menu.path === 'starter-templates' && astra_admin.starter_templates_data.is_available
										? ( <a index={ key } href={ astra_admin.starter_templates_data.redirection } target="_self" className={ `${
											activePage === menu.slug && activePath === menu.path
												? 'mb-4 sm:mb-0 border-astra text-astra active:text-astra focus:text-astra focus-visible:text-astra-hover hover:text-astra-hover inline-flex items-center px-1 border-b-2 text-sm leading-[0.875rem] font-medium'
												: 'mb-4 sm:mb-0 border-transparent text-slate-500 active:text-astra focus-visible:border-slate-300 focus-visible:text-slate-800 hover:border-slate-300 hover:text-slate-800 inline-flex items-center px-1 border-b-2 text-sm leading-[0.875rem] font-medium'
										}` }>
											{ menu.name }
										</a> )
										: (
											<Link
												index={ key }
												to={ {
													search: `?page=${ menu.slug }${
														'' !== menu.path ? '&path=' + menu.path : ''
													}`,
												} }
												className={ `${
													activePage === menu.slug && activePath === menu.path
														? 'mb-4 sm:mb-0 border-astra text-astra active:text-astra focus:text-astra focus-visible:text-astra-hover hover:text-astra-hover inline-flex items-center px-1 border-b-2 text-sm leading-[0.875rem] font-medium'
														: 'mb-4 sm:mb-0 border-transparent text-slate-600 active:text-astra focus-visible:border-slate-300 focus-visible:text-slate-800 hover:border-slate-300 hover:text-slate-800 inline-flex items-center px-1 border-b-2 text-sm leading-[0.875rem] font-medium'
												}` }
											>
												{ menu.name }
											</Link>
										)
									}
								</Fragment>
							) ) }
						</div>
					</div>
					{astra_admin.show_self_branding && (
						<div className="absolute bottom-2 lg:inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto ml-auto lg:ml-6 sm:pr-0">
							{!astra_admin.pro_available && (
								<div className="text-sm font-medium text-slate-600 pr-3 tablet:pr-2 border-r hover:text-[#1E293B] hover:svg-hover-color">
									<button
										onClick={redirectUpgradeAstraProPurchase}
										className="inline-flex items-center text-[#046BD2] hover:text-[#1E293B] focus-visible:text-[#1E293B]"
									>
										<svg
											width="16"
											height="12"
											viewBox="0 0 16 12"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
											className="mr-2 svg-focusable"
										>
											<path
												d="M3.3335 11.3337H12.6668M1.3335 0.666992L3.3335 8.66699H12.6668L14.6668 0.666992L10.6668 5.33366L8.00016 0.666992L5.3335 5.33366L1.3335 0.666992Z"
												stroke="#046BD2"
												strokeLinecap="round"
												strokeLinejoin="round"
												className="svg-path"
											/>
										</svg>
										{__('Unlock Pro Features', 'astra')}
									</button>
								</div>)}
							<div className="flex items-center text-[0.625rem] sm:text-sm font-medium leading-[1.375rem] text-slate-400 mr-1 sm:mr-3 divide-x divide-slate-200 gap-3 pl-1 sm:pl-3 border-r">
								<CoreVersion />
							</div>
							<div className="text-sm font-medium leading-6 text-slate-600 pr-3 tablet:pr-2">
								<DocsPopup />
							</div>

							<Tooltip
								text={__("Whats's New", "astra")}
								delay={50}
							>
								<div id="astra-whats-new"></div>
							</Tooltip>
						</div>
					)}
					{!astra_admin.show_self_branding && (
						<div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
							<div className="flex items-center whitespace-nowrap text-sm font-medium leading-[1.375rem] text-slate-400 mr-4 divide-x divide-slate-200 gap-3">
								<CoreVersion />
							</div>
						</div>
					)}
				</div>
			</div>
		</Disclosure>
	);
}
